import { Alert, Stack, Typography } from '@chronosphereio/chrono-ui';
import { useAuth, useSessionStorage } from '@chronosphereio/core';
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ExternalLink, Link, LoadingPane, LogoPane } from '@/components';
import { AuthRoutes, RouteConstants } from '@/model/Routes';
import { AUTH_ERROR_HEADER, Redirect } from '@/utils';
import { POST_LOGIN_REDIRECT_STORAGE_KEY } from './login/login-model';

export type CallbackProps = {
  useImpersonation?: boolean;
};

/**
 * View that processes login callbacks (i.e. redirects) from authentication.
 */
export function LoginPkceCallback({ useImpersonation }: CallbackProps = { useImpersonation: false }) {
  let verifyPrefix = '/auth/verify';
  let redirectRoute = AuthRoutes.LOGIN;
  if (useImpersonation) {
    verifyPrefix = '/auth/impersonate/verify';
    redirectRoute = AuthRoutes.IMPERSONATE;
  }

  const { refreshCurrentUser, currentUser, oktaClient } = useAuth();
  const navigate = useNavigate();
  const [redirectTo] = useSessionStorage<string | undefined>(POST_LOGIN_REDIRECT_STORAGE_KEY, undefined);
  const [accessToken, setAccessToken] = useState<undefined | string>();
  const [refreshToken, setRefreshToken] = useState<undefined | string>();
  const [verifyResponse, setVerifyResponse] = useState<undefined | Response>();

  const impersonationError = verifyResponse?.headers.get(AUTH_ERROR_HEADER);
  const hasAuthError = verifyResponse?.status === 401;

  // Parse params from the callback URL
  const [searchParams] = useSearchParams();
  const code = searchParams.get('code');

  useEffect(() => {
    async function getToken() {
      const response = await oktaClient?.token.parseFromUrl();
      setAccessToken(response?.tokens.accessToken?.accessToken);
      setRefreshToken(response?.tokens.refreshToken?.refreshToken);
      return response;
    }
    getToken().catch((err) => {
      throw err;
    });
  }, [code, oktaClient, accessToken, refreshToken]);

  useEffect(() => {
    if (accessToken) {
      const verifyParams = new URLSearchParams();
      verifyParams.append('token', accessToken);
      fetch(`${verifyPrefix}?${verifyParams.toString()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      }).then((response) => {
        setVerifyResponse(response);
      });
    }
  }, [accessToken, refreshToken, verifyPrefix]);

  useEffect(() => {
    if (verifyResponse !== undefined && !hasAuthError && refreshCurrentUser !== undefined) {
      refreshCurrentUser();
    }
  }, [verifyResponse, hasAuthError, refreshCurrentUser]);

  useEffect(() => {
    if (currentUser !== undefined && verifyResponse !== undefined && !hasAuthError) {
      const nextUrl = redirectTo !== undefined ? redirectTo : RouteConstants.HOME;
      navigate(nextUrl, { replace: true });
    }
  }, [currentUser, navigate, redirectTo, verifyResponse, hasAuthError]);

  if (impersonationError) {
    return (
      <LogoPane>
        <Alert severity="error" sx={{ alignItems: 'center' }}>
          <Typography>
            <Stack spacing={0.5} component="span">
              <span>
                {impersonationError} For more information,{' '}
                <ExternalLink
                  color="inherit"
                  href="https://stackoverflowteams.com/c/chronosphere/questions/155"
                  rel="noopener noreferrer"
                >
                  go to StackOverflow.
                </ExternalLink>
              </span>
              <Link to={AuthRoutes.IMPERSONATE} color="inherit">
                Click to return to login
              </Link>
            </Stack>
          </Typography>
        </Alert>
      </LogoPane>
    );
  }

  // If coming directly from the IDP to login, just redirect to login page
  // so it can log them in
  if (code === null) {
    return <Redirect to={`${redirectRoute}?${searchParams.toString()}`} />;
  }

  // Wait for verify above
  return <LoadingPane text="Logging In" />;
}
